import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API_URL } from '../../config/api';
import {
  WORKFLOW_REACTION_CATEGORIES,
  getReactionDisplay,
  WORKFLOW_CLOSED_STATUS_ID,
} from '../../utils/reactionConfig';
import { compareUrgentFirst, isOpenUrgentWorkflow } from '../../utils/urgentDeadlineUtils';
import UrgentCountdownBadge from './UrgentCountdownBadge';
import UrgentTag, { UrgentRowShell } from './UrgentTag';
import { collectLocalChannelMessages, workflowMetricsFromMessages } from '../../realm/offlineLookups';
import { realmAvailable } from '../../realm';
import { ChevronDownIcon } from './SidebarIcons';
import WorkflowViewDialog from './WorkflowViewDialog';
import ImageViewerModal from './ImageViewerModal';
import { addReaction, loadOlderMessages } from '../../store/slices/chatSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { downloadAttachment, openAttachmentFallback } from '../../utils/fileUtils';
import { useCompletionTimeDialog } from './CompletionTimeDialog';
import WorkItemTimer from '../common/WorkItemTimer';
import {
  compareWorkTimerPriority,
  ensureWorkTimers,
  subscribeWorkTimers,
} from '../../services/workTimerStore';
import MagicPickModal, { MagicWandIcon } from '../common/MagicPickModal';
import { fetchTasks, fetchTasksByMapTaskId } from '../../store/slices/taskSlice';
import { resolveWorkflowCloseMinutes, workflowLinkedTasksFrom, loadMappedByTaskId, apiErrorMessage } from '../../utils/workflowTaskUtils';

const WORK_ITEM_TYPES = WORKFLOW_REACTION_CATEGORIES.find((c) => c.id === 'work-item-type')?.reactions || [];
const WORKFLOW_STATUSES = WORKFLOW_REACTION_CATEGORIES.find((c) => c.id === 'workflow-status')?.reactions || [];
const BUG_TYPE_ID = 'wf:work-item-type:bug';
const OTHER_SECTION_ID = '__other__';
const UNKNOWN_SENDER_ID = '__unknown_sender__';
const UNKNOWN_DATE_ID = '__unknown_date__';
const NO_STATUS_ID = '__no_status__';
const UNASSIGNED_ID = '__unassigned__';
const UNTAGGED_ID = '__untagged__';
const METRICS_LOAD_MORE_SIZE = 500;

const oldestMessageCreatedAt = (messages = []) => {
  let oldest = null;
  (messages || []).forEach((msg) => {
    const createdAt = msg?.createdAt || msg?.timestamp;
    if (!createdAt) return;
    const time = new Date(createdAt).getTime();
    if (!Number.isFinite(time)) return;
    if (!oldest || time < oldest.time) {
      oldest = { time, createdAt };
    }
  });
  return oldest?.createdAt || null;
};

const FilterFunnelIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h18l-7 8v5l-4 2v-7L3 5z" />
  </svg>
);

const PlusIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
  </svg>
);

const SelectChevronIcon = ({ className = '' }) => (
  <svg className={`w-3.5 h-3.5 ${className}`.trim()} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const filterSelectClass =
  'w-full appearance-none [-webkit-appearance:none] [appearance:none] text-[13px] font-medium rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white py-2 pl-3 pr-8 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400';
const filterDateClass =
  'w-full text-[13px] font-medium rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white py-2 px-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400';

const FilterField = ({ icon, label, children }) => (
  <div className="min-w-0 rounded-2xl bg-gray-50/90 dark:bg-gray-900/40 border border-gray-200/80 dark:border-gray-700/80 p-2.5">
    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
      <span className="text-primary-500 dark:text-primary-400">{icon}</span>
      {label}
    </p>
    {children}
  </div>
);

const FilterSelect = ({ value, onChange, children }) => (
  <div className="relative overflow-hidden">
    <select value={value} onChange={onChange} className={filterSelectClass}>
      {children}
    </select>
    <SelectChevronIcon className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
  </div>
);

const DateModePills = ({ value, onChange }) => {
  const options = [
    { id: 'all', label: 'All dates' },
    { id: 'today', label: 'Raised today' },
    { id: 'between', label: 'Date between' },
  ];
  return (
    <div className="flex p-0.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-sm">
      {options.map((option) => {
        const selected = value === option.id;
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onChange(option.id)}
            className={`flex-1 min-w-0 px-2 py-1.5 rounded-[10px] text-[11px] font-semibold truncate transition-colors ${
              selected
                ? 'bg-primary-500 text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 max-w-full rounded-full bg-primary-50 dark:bg-primary-950/40 text-primary-800 dark:text-primary-200 border border-primary-200/80 dark:border-primary-800 px-2.5 py-1 text-[11px] font-semibold">
    <span className="truncate">{label}</span>
    <button
      type="button"
      onClick={onRemove}
      className="flex-shrink-0 rounded-full p-0.5 hover:bg-primary-100 dark:hover:bg-primary-900/60"
      aria-label={`Remove ${label}`}
    >
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </span>
);

const MaximizeIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 9V5h4M20 9V5h-4M4 15v4h4M20 15v4h-4" />
  </svg>
);

const MinimizeIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4H5v4M15 4h4v4M9 20H5v-4M15 20h4v-4" />
  </svg>
);

const RefreshIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const chipBase =
  'inline-flex items-center gap-0.5 max-w-[9rem] min-h-[1.375rem] px-1.5 py-0.5 rounded-md text-[10px] font-semibold leading-tight truncate border';

const CHIP_TONE = {
  violet: 'border-violet-200/90 bg-violet-50 text-violet-800 dark:border-violet-700/60 dark:bg-violet-900/25 dark:text-violet-200',
  indigo: 'border-indigo-200/90 bg-indigo-50 text-indigo-800 dark:border-indigo-700/60 dark:bg-indigo-900/25 dark:text-indigo-200',
  blue: 'border-sky-200/90 bg-sky-50 text-sky-800 dark:border-sky-700/60 dark:bg-sky-900/25 dark:text-sky-200',
  cyan: 'border-cyan-200/90 bg-cyan-50 text-cyan-800 dark:border-cyan-700/60 dark:bg-cyan-900/25 dark:text-cyan-200',
  teal: 'border-teal-200/90 bg-teal-50 text-teal-800 dark:border-teal-700/60 dark:bg-teal-900/25 dark:text-teal-200',
  green: 'border-emerald-200/90 bg-emerald-50 text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-900/25 dark:text-emerald-200',
  amber: 'border-amber-200/90 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/25 dark:text-amber-100',
  red: 'border-rose-200/90 bg-rose-50 text-rose-800 dark:border-rose-700/60 dark:bg-rose-900/25 dark:text-rose-200',
  slate: 'border-gray-200/90 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700/40 dark:text-gray-300',
};

const chipTone = (tone) => CHIP_TONE[tone] || CHIP_TONE.slate;

const METRIC_COLORS = {
  total: '#6366F1',
  pending: '#F59E0B',
  completed: '#10B981',
};

const isItemClosed = (item) =>
  Object.values(item?.workflowByCategory || {}).includes(WORKFLOW_CLOSED_STATUS_ID);

const countClosed = (list) => (list || []).filter(isItemClosed).length;

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-2.5 py-1.5 shadow-sm">
      <p className="text-[11px] font-semibold text-gray-800 dark:text-gray-100">
        {row.name}: <span className="tabular-nums">{row.value}</span>
      </p>
    </div>
  );
};

const OverviewMetricsChart = ({ total, pending, completed, expanded }) => {
  const barData = [
    { name: 'Total', value: total, fill: METRIC_COLORS.total },
    { name: 'Pending approval', value: pending, fill: METRIC_COLORS.pending },
    { name: 'Completed', value: completed, fill: METRIC_COLORS.completed },
  ];
  const metricCards = [
    { label: 'Total', value: total, color: 'text-indigo-600 dark:text-indigo-300', bg: 'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-100 dark:border-indigo-900/50' },
    { label: 'Pending approval', value: pending, color: 'text-amber-600 dark:text-amber-300', bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-100 dark:border-amber-900/50' },
    { label: 'Completed', value: completed, color: 'text-emerald-600 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-100 dark:border-emerald-900/50' },
  ];

  return (
    <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-800 shadow-sm px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
        Overview
      </p>
      <div className={`grid grid-cols-[minmax(6.75rem,0.85fr)_minmax(0,1.3fr)] items-stretch ${expanded ? 'gap-3' : 'gap-2'}`}>
        <div className="flex flex-col gap-1 min-w-0">
          {metricCards.map((card) => (
            <div key={card.label} className={`rounded-lg border px-2 py-1 flex items-center justify-between gap-2 min-w-0 ${card.bg}`}>
              <p className="text-[9px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">
                {card.label}
              </p>
              <p className={`text-sm font-black tabular-nums leading-none ${card.color}`}>
                {card.value}
              </p>
            </div>
          ))}
        </div>

        <div className={`min-w-0 h-full ${expanded ? 'min-h-[4.75rem]' : 'min-h-[4.25rem]'}`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 6, left: 0, bottom: 0 }}>
              <XAxis type="number" hide domain={[0, Math.max(total, 1)]} />
              <YAxis
                type="category"
                dataKey="name"
                width={expanded ? 108 : 92}
                tick={{ fontSize: expanded ? 10 : 8, fill: 'currentColor' }}
                className="text-gray-600 dark:text-gray-300"
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={expanded ? 12 : 9} maxBarSize={14}>
                {barData.map((row) => (
                  <Cell key={row.name} fill={row.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const SectionStatusPie = ({ items, size = 72, innerRadius = 22, outerRadius = 34, showLegend = true }) => {
  const completed = countClosed(items);
  const pending = (items?.length || 0) - completed;
  const pieData = [
    { name: 'Pending approval', value: pending, fill: METRIC_COLORS.pending },
    { name: 'Completed', value: completed, fill: METRIC_COLORS.completed },
  ].filter((row) => row.value > 0);
  const donutData = pieData.length
    ? pieData
    : [{ name: 'None', value: 1, fill: '#E5E7EB' }];

  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={donutData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              stroke="#ffffff"
              strokeWidth={1.5}
              paddingAngle={pieData.length > 1 ? 2 : 0}
            >
              {donutData.map((row) => (
                <Cell key={row.name} fill={row.fill} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-sm font-black tabular-nums text-gray-900 dark:text-white leading-none">
            {items?.length || 0}
          </span>
        </div>
      </div>
      {showLegend ? (
        <ul className="space-y-1 text-[11px] leading-tight text-gray-600 dark:text-gray-300 min-w-0">
          <li className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: METRIC_COLORS.pending }} />
            Pending approval <span className="ml-auto pl-3 font-bold tabular-nums">{pending}</span>
          </li>
          <li className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: METRIC_COLORS.completed }} />
            Completed <span className="ml-auto pl-3 font-bold tabular-nums">{completed}</span>
          </li>
        </ul>
      ) : null}
    </div>
  );
};

const getTicketText = (content, maxLen = 80) => {
  const trimmed = (content || '').trim();
  if (!trimmed) return 'Workflow item';
  return trimmed.length <= maxLen ? trimmed : `${trimmed.slice(0, maxLen - 1)}…`;
};

const renderDescription = (item) => {
  const value = [item?.workflowDescription, item?.contentHtml, item?.content]
    .find((entry) => typeof entry === 'string' && entry.trim()) || '';
  if (!value) {
    return <p className="text-[12px] text-gray-400 dark:text-gray-500">No description</p>;
  }
  if (value.trim().startsWith('<') && value.includes('</')) {
    return (
      <div
        className="ql-editor-content text-[13px] text-gray-700 dark:text-gray-300 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md"
        dangerouslySetInnerHTML={{ __html: value }}
        style={{ fontSize: '0.8125rem', lineHeight: '1.6' }}
      />
    );
  }
  return (
    <p className="text-[13px] text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words">
      {value}
    </p>
  );
};

const buildWorkItemSections = (items) => {
  const buckets = new Map();
  items.forEach((item) => {
    const typeId = item.workflowByCategory?.['work-item-type'] || OTHER_SECTION_ID;
    if (!buckets.has(typeId)) buckets.set(typeId, []);
    buckets.get(typeId).push(item);
  });

  const sections = [];
  const pushSection = (id, label, emoji, sectionItems) => {
    if (!sectionItems?.length) return;
    sections.push({ id, label, emoji, items: sectionItems, count: sectionItems.length });
  };

  if (buckets.has(BUG_TYPE_ID)) {
    const bugDisplay = getReactionDisplay(BUG_TYPE_ID);
    pushSection(BUG_TYPE_ID, bugDisplay?.label || 'Bug', bugDisplay?.emoji || '🐞', buckets.get(BUG_TYPE_ID));
    buckets.delete(BUG_TYPE_ID);
  }

  WORK_ITEM_TYPES.forEach((type) => {
    if (type.id === BUG_TYPE_ID || !buckets.has(type.id)) return;
    pushSection(type.id, type.label, type.emoji, buckets.get(type.id));
    buckets.delete(type.id);
  });

  if (buckets.has(OTHER_SECTION_ID)) {
    pushSection(OTHER_SECTION_ID, 'Other Workflow', '📋', buckets.get(OTHER_SECTION_ID));
    buckets.delete(OTHER_SECTION_ID);
  }

  buckets.forEach((sectionItems, typeId) => {
    const display = getReactionDisplay(typeId);
    pushSection(typeId, display?.label || 'Workflow', display?.emoji || '📋', sectionItems);
  });

  return sections;
};

const parseItemDate = (item) => {
  const raw = item?.createdAt || item?.timestamp;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const itemTimestamp = (item) => parseItemDate(item)?.getTime() || 0;

const localDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return UNKNOWN_DATE_ID;
  return date.toLocaleDateString('en-CA');
};

const todayKey = () => localDateKey(new Date());

const formatDateHeading = (key) => {
  if (!key || key === UNKNOWN_DATE_ID) return 'Unknown date';
  if (key === todayKey()) return 'Today';
  const date = new Date(`${key}T00:00:00`);
  if (Number.isNaN(date.getTime())) return key;
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatRaisedAt = (item) => {
  const date = parseItemDate(item);
  if (!date) return '';
  const start = (input) => {
    const next = new Date(input);
    next.setHours(0, 0, 0, 0);
    return next.getTime();
  };
  const diffDays = Math.round((start(new Date()) - start(date)) / 86400000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
};

const sortItemsByTime = (list, direction) => {
  const dir = direction === 'asc' ? 1 : -1;
  return [...(list || [])].sort((a, b) => {
    const urgentDiff = compareUrgentFirst(a, b, { isUrgent: isOpenUrgentWorkflow });
    if (urgentDiff !== 0) return urgentDiff;
    const closedA = isItemClosed(a) ? 1 : 0;
    const closedB = isItemClosed(b) ? 1 : 0;
    if (closedA !== closedB) return closedA - closedB;
    return (itemTimestamp(a) - itemTimestamp(b)) * dir;
  });
};

const getItemTypeId = (item) => item.workflowByCategory?.['work-item-type'] || OTHER_SECTION_ID;

const getItemStatusId = (item) => item.workflowByCategory?.['workflow-status'] || NO_STATUS_ID;

const getItemSenderId = (item) => item.senderId || UNKNOWN_SENDER_ID;

const getItemAssigneeId = (item) => item.workflowAssignedToUserId || UNASSIGNED_ID;

const getItemTagKey = (tag) => tag?.id || tag?.label || '';

const normalizeTagLabel = (tag) =>
  String(tag?.label || tag?.id || '').trim().toLowerCase();

const itemHasTag = (item, tagKey) => {
  const wanted = String(tagKey || '').trim().toLowerCase();
  if (!wanted) return false;
  return (item?.customTags || []).some((tag) => {
    const label = normalizeTagLabel(tag);
    return label === wanted || String(getItemTagKey(tag)).toLowerCase() === wanted;
  });
};

const getItemSearchText = (item) => {
  const tags = (item?.customTags || []).map((tag) => tag?.label || '').join(' ');
  return [
    item?.content,
    item?.workflowTitle,
    item?.workflowDescription,
    item?.senderName,
    item?.workflowAssignedToName,
    item?.workflowTrackId,
    tags,
    item?.sourceLabel,
  ].filter(Boolean).join(' ').toLowerCase();
};

const buildGroupedSections = (items, groupBy, sortDir) => {
  const sorted = sortItemsByTime(items, sortDir);
  if (groupBy === 'workflow') {
    return buildWorkItemSections(sorted);
  }

  const buckets = new Map();
  sorted.forEach((item) => {
    let id;
    let label;
    let emoji = '📋';
    if (groupBy === 'date') {
      const date = parseItemDate(item);
      id = date ? localDateKey(date) : UNKNOWN_DATE_ID;
      label = formatDateHeading(id);
      emoji = '📅';
    } else if (groupBy === 'raisedBy') {
      id = getItemSenderId(item);
      label = item.senderName || 'Unknown';
      emoji = '👤';
    } else {
      id = getItemStatusId(item);
      const display = id === NO_STATUS_ID ? null : getReactionDisplay(id);
      label = display?.label || 'No status';
      emoji = display?.emoji || '⚪';
    }
    if (!buckets.has(id)) buckets.set(id, { id, label, emoji, items: [] });
    buckets.get(id).items.push(item);
  });

  let sections = Array.from(buckets.values()).map((section) => ({
    ...section,
    count: section.items.length,
  }));

  if (groupBy === 'status') {
    const order = WORKFLOW_STATUSES.map((status) => status.id);
    sections.sort((a, b) => {
      const closedA = a.id === WORKFLOW_CLOSED_STATUS_ID ? 1 : 0;
      const closedB = b.id === WORKFLOW_CLOSED_STATUS_ID ? 1 : 0;
      if (closedA !== closedB) return closedA - closedB;
      const ai = a.id === NO_STATUS_ID ? order.length + 1 : order.indexOf(a.id);
      const bi = b.id === NO_STATUS_ID ? order.length + 1 : order.indexOf(b.id);
      return (ai < 0 ? order.length : ai) - (bi < 0 ? order.length : bi);
    });
  } else if (groupBy === 'raisedBy') {
    sections.sort((a, b) => a.label.localeCompare(b.label));
  } else if (groupBy === 'date') {
    sections.sort((a, b) => {
      if (a.id === UNKNOWN_DATE_ID) return 1;
      if (b.id === UNKNOWN_DATE_ID) return -1;
      return sortDir === 'asc' ? a.id.localeCompare(b.id) : b.id.localeCompare(a.id);
    });
  }

  return sections;
};

const MetricsFilters = ({
  open,
  onOpenChange,
  dateMode,
  onDateModeChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  raisedBy,
  onRaisedByChange,
  raisedByOptions,
  assignedTo,
  onAssignedToChange,
  assignedToOptions = [],
  currentUserId,
  assignedToMeSearch = '',
  onAssignedToMeSearchChange,
  statusOpen,
  statusClosed,
  onStatusOpenChange,
  onStatusClosedChange,
  onToggleAssignedToMe,
  workflowType,
  onWorkflowTypeChange,
  workflowStatus = '',
  onWorkflowStatusChange,
  tag = '',
  onTagChange,
  tagOptions = [],
  groupBy,
  onGroupByChange,
  sortDir,
  onSortDirChange,
  showWorkflowItems = true,
  onShowWorkflowItemsChange,
  showPersonalItems = true,
  onShowPersonalItemsChange,
  hasActiveFilters,
  onClear,
}) => {
  const raisedByLabel = raisedByOptions.find((person) => person.id === raisedBy)?.name;
  const assignedToLabel = assignedTo === UNASSIGNED_ID
    ? 'Unassigned'
    : assignedToOptions.find((person) => person.id === assignedTo)?.name;
  const assignedToMe = Boolean(currentUserId && assignedTo === currentUserId);
  const typeLabel = workflowType === OTHER_SECTION_ID
    ? 'Other workflow'
    : WORK_ITEM_TYPES.find((type) => type.id === workflowType)?.label;
  const statusLabel = workflowStatus === NO_STATUS_ID
    ? 'No status'
    : WORKFLOW_STATUSES.find((status) => status.id === workflowStatus)?.label;
  const tagLabel = tag === UNTAGGED_ID
    ? 'Untagged'
    : tagOptions.find((option) => option.id === tag)?.label;
  const groupLabel = {
    workflow: 'Workflow',
    date: 'Date',
    raisedBy: 'Raised by',
    status: 'Status',
  }[groupBy];
  const dateChip = dateMode === 'today'
    ? 'Raised today'
    : dateMode === 'between'
      ? `Raised ${dateFrom || '…'} – ${dateTo || '…'}`
      : null;

  return (
    <div className="px-3 pt-3 pb-0 flex-shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        <button
          type="button"
          onClick={() => onOpenChange(!open)}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold border transition-colors flex-shrink-0 ${
            open || hasActiveFilters
              ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-300'
          }`}
          aria-expanded={open}
        >
          {open ? <FilterFunnelIcon className="w-3.5 h-3.5" /> : <PlusIcon className="w-3.5 h-3.5" />}
          {open ? 'Hide filters' : 'Filter'}
        </button>
        <label className="relative inline-flex items-center flex-1 min-w-0">
          <span className="sr-only">Search assigned workflows</span>
          <svg className="pointer-events-none absolute left-2.5 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
          </svg>
          <input
            type="search"
            value={assignedToMeSearch}
            onChange={(event) => onAssignedToMeSearchChange(event.target.value)}
            placeholder="Search"
            className="w-full min-w-0 h-8 pl-8 pr-3 text-[12px] rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
          />
        </label>
      </div>
      <div className="flex items-center gap-2 flex-wrap mt-2">
        <button
          type="button"
          onClick={onToggleAssignedToMe}
          disabled={!currentUserId}
          className={`inline-flex items-center px-3 py-[2px] rounded-full text-[12px] font-semibold border transition-colors ${
            assignedToMe
              ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-300'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          aria-pressed={assignedToMe}
        >
          Only assigned to me
        </button>
        <button
          type="button"
          onClick={onStatusOpenChange}
          className={`inline-flex items-center px-3 py-[2px] rounded-full text-[12px] font-semibold border transition-colors ${
            statusOpen
              ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-300'
          }`}
          aria-pressed={statusOpen}
        >
          Open
        </button>
        <button
          type="button"
          onClick={onStatusClosedChange}
          className={`inline-flex items-center px-3 py-[2px] rounded-full text-[12px] font-semibold border transition-colors ${
            statusClosed
              ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-300'
          }`}
          aria-pressed={statusClosed}
        >
          Close
        </button>
        {!open && dateChip ? (
          <FilterChip label={dateChip} onRemove={() => { onDateModeChange('all'); onDateFromChange(''); onDateToChange(''); }} />
        ) : null}
        {!open && raisedByLabel ? (
          <FilterChip label={raisedByLabel} onRemove={() => onRaisedByChange('')} />
        ) : null}
        {!open && assignedToLabel && !assignedToMe ? (
          <FilterChip label={`Assigned: ${assignedToLabel}`} onRemove={() => onAssignedToChange('')} />
        ) : null}
        {!open && typeLabel ? (
          <FilterChip label={typeLabel} onRemove={() => onWorkflowTypeChange('')} />
        ) : null}
        {!open && statusLabel ? (
          <FilterChip label={statusLabel} onRemove={() => onWorkflowStatusChange('')} />
        ) : null}
        {!open && tagLabel ? (
          <FilterChip label={`Tag: ${tagLabel}`} onRemove={() => onTagChange('')} />
        ) : null}
        {!open && groupBy !== 'workflow' ? (
          <FilterChip label={`Group: ${groupLabel}`} onRemove={() => onGroupByChange('workflow')} />
        ) : null}
        {!open && sortDir === 'asc' ? (
          <FilterChip label="Oldest first" onRemove={() => onSortDirChange('desc')} />
        ) : null}
        {!open && !showWorkflowItems ? (
          <FilterChip label="Hide workflow" onRemove={() => onShowWorkflowItemsChange?.(true)} />
        ) : null}
        {!open && !showPersonalItems ? (
          <FilterChip label="Hide personal" onRemove={() => onShowPersonalItemsChange?.(true)} />
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2">
        <label className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-gray-700 dark:text-gray-200 cursor-pointer">
          <input
            type="checkbox"
            checked={showWorkflowItems}
            onChange={(event) => onShowWorkflowItemsChange?.(event.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
          />
          Workflow
        </label>
        <label className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-gray-700 dark:text-gray-200 cursor-pointer">
          <input
            type="checkbox"
            checked={showPersonalItems}
            onChange={(event) => onShowPersonalItemsChange?.(event.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
          />
          Personal
        </label>
        <label className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-semibold text-gray-700 dark:text-gray-200">
          <span className="whitespace-nowrap">Sort</span>
          <select
            value={sortDir}
            onChange={(event) => onSortDirChange(event.target.value)}
            className="px-2 py-1 text-[12px] rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
        </label>
      </div>

      {open ? (
        <div className="mt-2.5 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-800 shadow-sm p-3">
          <div className="flex items-center justify-between gap-2 mb-3">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Filter workflows</p>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={onClear}
                className="text-[12px] font-semibold text-primary-600 dark:text-primary-400 hover:underline"
              >
                Clear all
              </button>
            ) : null}
          </div>

          <div className="grid gap-2.5">
            <FilterField
              label="Date"
              icon={(
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M5 11h14M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              )}
            >
              <DateModePills value={dateMode} onChange={onDateModeChange} />
              {dateMode === 'between' ? (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <label className="min-w-0">
                    <span className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 mb-1">From</span>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(event) => onDateFromChange(event.target.value)}
                      className={filterDateClass}
                    />
                  </label>
                  <label className="min-w-0">
                    <span className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 mb-1">To</span>
                    <input
                      type="date"
                      value={dateTo}
                      min={dateFrom || undefined}
                      onChange={(event) => onDateToChange(event.target.value)}
                      className={filterDateClass}
                    />
                  </label>
                </div>
              ) : null}
            </FilterField>

            <div className="grid grid-cols-2 gap-2.5">
              <FilterField
                label="Assigned to"
                icon={(
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m6-4a4 4 0 11-8 0 4 4 0 018 0zm6 0a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              >
                <FilterSelect value={assignedTo} onChange={(event) => onAssignedToChange(event.target.value)}>
                  <option value="">Anyone</option>
                  <option value={UNASSIGNED_ID}>Unassigned</option>
                  {assignedToOptions.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.name}{currentUserId && person.id === currentUserId ? ' (You)' : ''}
                    </option>
                  ))}
                </FilterSelect>
              </FilterField>

              <FilterField
                label="Raised by"
                icon={(
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                )}
              >
                <FilterSelect value={raisedBy} onChange={(event) => onRaisedByChange(event.target.value)}>
                  <option value="">Anyone</option>
                  {raisedByOptions.map((person) => (
                    <option key={person.id} value={person.id}>{person.name}</option>
                  ))}
                </FilterSelect>
              </FilterField>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <FilterField
                label="Workflow item"
                icon={(
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h7" />
                  </svg>
                )}
              >
                <FilterSelect value={workflowType} onChange={(event) => onWorkflowTypeChange(event.target.value)}>
                  <option value="">All types</option>
                  {WORK_ITEM_TYPES.map((type) => (
                    <option key={type.id} value={type.id}>{type.emoji} {type.label}</option>
                  ))}
                  <option value={OTHER_SECTION_ID}>Other workflow</option>
                </FilterSelect>
              </FilterField>

              <FilterField
                label="Status"
                icon={(
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              >
                <FilterSelect value={workflowStatus} onChange={(event) => onWorkflowStatusChange(event.target.value)}>
                  <option value="">All statuses</option>
                  {WORKFLOW_STATUSES.map((status) => (
                    <option key={status.id} value={status.id}>{status.emoji} {status.label}</option>
                  ))}
                  <option value={NO_STATUS_ID}>No status</option>
                </FilterSelect>
              </FilterField>
            </div>

            <FilterField
              label="Tags"
              icon={(
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M3 11l8.5-8.5a2 2 0 012.83 0L21 9.17a2 2 0 010 2.83L12.5 20.5a2 2 0 01-2.83 0L3 14V5a2 2 0 012-2h6z" />
                </svg>
              )}
            >
              <FilterSelect value={tag} onChange={(event) => onTagChange(event.target.value)}>
                <option value="">All tags</option>
                <option value={UNTAGGED_ID}>Untagged</option>
                {tagOptions.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </FilterSelect>
            </FilterField>

            <div className="grid grid-cols-2 gap-2.5">
              <FilterField
                label="Group by"
                icon={(
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h7v5H4V6zm9 0h7v9h-7V6zM4 13h7v5H4v-5z" />
                  </svg>
                )}
              >
                <FilterSelect value={groupBy} onChange={(event) => onGroupByChange(event.target.value)}>
                  <option value="workflow">Workflow</option>
                  <option value="date">Date</option>
                  <option value="raisedBy">Raised by</option>
                  <option value="status">Status</option>
                </FilterSelect>
              </FilterField>

              <FilterField
                label="Sort by"
                icon={(
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h12M3 12h8M3 17h4M17 7v10m0 0l-3-3m3 3l3-3" />
                  </svg>
                )}
              >
                <FilterSelect value={sortDir} onChange={(event) => onSortDirChange(event.target.value)}>
                  <option value="desc">Newest first</option>
                  <option value="asc">Oldest first</option>
                </FilterSelect>
              </FilterField>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const ManagementMetricsPanel = ({
  channel,
  onClose,
  onMessageClick,
  isExpanded = false,
  onToggleExpand,
}) => {
  const dispatch = useDispatch();
  const { promptCompletionTime, completionTimeDialog } = useCompletionTimeDialog();
  const currentUserId = useSelector((state) => state.auth.user?.id);
  const isDirectMessage = channel?.type === 'direct' || channel?.type === 'DIRECT_MESSAGE';
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [expandedWorkflowIds, setExpandedWorkflowIds] = useState(new Set());
  const [viewingWorkflow, setViewingWorkflow] = useState(null);
  const [magicPickOpen, setMagicPickOpen] = useState(false);
  const [workTimerTick, setWorkTimerTick] = useState(0);
  const [completingWorkflowId, setCompletingWorkflowId] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [dateMode, setDateMode] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [raisedBy, setRaisedBy] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusClosed, setStatusClosed] = useState(false);
  const [assignedToMeSearch, setAssignedToMeSearch] = useState('');
  const [workflowType, setWorkflowType] = useState('');
  const [workflowStatus, setWorkflowStatus] = useState('');
  const [tag, setTag] = useState('');
  const [groupBy, setGroupBy] = useState('workflow');
  const [sortDir, setSortDir] = useState('desc');
  const [showWorkflowItems, setShowWorkflowItems] = useState(true);
  const [showPersonalItems, setShowPersonalItems] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const reduxChannelMessages = useSelector((state) =>
    channel?.id ? (state.chat.messages[channel.id] || []) : []
  );
  const localMessagesRef = useRef([]);
  localMessagesRef.current = reduxChannelMessages;

  const enrichItems = useCallback((rawItems, messages) => {
    const byId = new Map((messages || []).map((msg) => [String(msg.id || msg._id), msg]));
    return (rawItems || []).map((item) => {
      const msg = byId.get(String(item.messageId));
      return {
        ...item,
        channelId: item.channelId || channel?.id || msg?.channelId,
        channelName: item.channelName || channel?.name,
        channelType: item.channelType || channel?.type,
        sourceLabel: item.sourceLabel || (isDirectMessage ? `@${item.senderName || 'DM'}` : `#${channel?.name || ''}`),
        contentHtml: item.contentHtml || msg?.content || '',
        workflowDescription: item.workflowDescription || msg?.workflowDescription || null,
        workflowTitle: item.workflowTitle || msg?.workflowTitle || null,
        customTags: item.customTags || msg?.customTags || [],
        senderProfilePicture: item.senderProfilePicture || msg?.senderProfilePicture || null,
        attachments: item.attachments || msg?.attachments || [],
        workflowAssignedToUserId: item.workflowAssignedToUserId || msg?.workflowAssignedToUserId || null,
        workflowAssignedToName: item.workflowAssignedToName || msg?.workflowAssignedToName || null,
        completionTimeInMinutes: item.completionTimeInMinutes || msg?.completionTimeInMinutes || null,
        urgentDeadlineAt: item.urgentDeadlineAt || msg?.urgentDeadlineAt || null,
        createdAt: item.createdAt || msg?.createdAt || msg?.timestamp || null,
        timestamp: item.timestamp || item.createdAt || msg?.timestamp || msg?.createdAt || null,
        senderId: item.senderId || msg?.senderId || null,
        senderName: item.senderName || msg?.senderName || 'Unknown',
      };
    });
  }, [channel, isDirectMessage]);

  const upsertMetricsItem = useCallback((messageId) => {
    if (!messageId) return false;
    const messages = localMessagesRef.current || [];
    const msg = messages.find((item) => String(item.id || item._id) === String(messageId));
    if (!msg) return false;
    const nextItem = enrichItems(workflowMetricsFromMessages([msg]), [msg])[0] || null;
    setItems((prev) => {
      const idx = prev.findIndex((item) => String(item.messageId) === String(messageId));
      if (!nextItem) {
        if (idx === -1) return prev;
        return prev.filter((_, index) => index !== idx);
      }
      if (idx === -1) return [nextItem, ...prev];
      const copy = [...prev];
      copy[idx] = { ...prev[idx], ...nextItem };
      return copy;
    });
    setViewingWorkflow((prev) => {
      if (!prev || String(prev.messageId) !== String(messageId)) return prev;
      return nextItem ? { ...prev, ...nextItem } : null;
    });
    return true;
  }, [enrichItems]);

  const fetchMetrics = useCallback(async ({ force = false, silent = false } = {}) => {
    if (!channel?.id) return;
    if (!silent) {
      setLoading(true);
      setError(null);
    }

    const applyLocalMetrics = async () => {
      const localMessages = await collectLocalChannelMessages(channel.id, localMessagesRef.current);
      const localItems = enrichItems(workflowMetricsFromMessages(localMessages), localMessages);
      setItems(localItems);
      return localItems.length > 0;
    };

    const online = typeof navigator === 'undefined' || navigator.onLine !== false;

    if (!force) {
      const hadLocal = await applyLocalMetrics();
      if (hadLocal || (await realmAvailable())) {
        if (!silent) setLoading(false);
        return;
      }
    }

    if (!online) {
      await applyLocalMetrics();
      if (!silent) setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/messages/channel/${channel.id}/workflow-metrics`,
        {
          params: { category: 'work-item-type' },
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        },
      );
      const localMessages = await collectLocalChannelMessages(channel.id, localMessagesRef.current);
      setItems(enrichItems(response.data?.items || [], localMessages));
      setError(null);
    } catch (err) {
      if (!force) {
        await applyLocalMetrics();
        setError(null);
      } else {
        await applyLocalMetrics();
        if (!silent) {
          setError(err?.response?.data?.error || err?.message || 'Failed to refresh metrics');
        }
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [channel?.id, enrichItems]);

  const handleRefreshMetrics = async () => {
    if (!channel?.id || loading || refreshing) return;
    setRefreshing(true);
    try {
      await fetchMetrics({ force: true, silent: true });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setHasMoreMessages(true);
    setLoadingMore(false);
  }, [channel?.id]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const handleLoadMore = useCallback(async () => {
    if (!channel?.id || loadingMore || !hasMoreMessages) return;
    setLoadingMore(true);
    try {
      const localMessages = await collectLocalChannelMessages(channel.id, localMessagesRef.current);
      const beforeDate = oldestMessageCreatedAt(localMessages) || new Date().toISOString();
      const result = await dispatch(loadOlderMessages({
        channelId: channel.id,
        size: METRICS_LOAD_MORE_SIZE,
        beforeDate,
      }));
      if (result.type !== 'chat/loadOlderMessages/fulfilled') {
        dispatch(addNotification({
          type: 'error',
          message: result.payload || 'Failed to load more messages',
        }));
        return;
      }
      const loaded = result.payload?.messages || [];
      const offlineEmpty = Boolean(result.payload?.fromRealm)
        && loaded.length === 0
        && typeof navigator !== 'undefined'
        && navigator.onLine === false;
      if (!offlineEmpty) {
        setHasMoreMessages(loaded.length >= METRICS_LOAD_MORE_SIZE);
      }
      const refreshed = await collectLocalChannelMessages(channel.id, [
        ...(localMessagesRef.current || []),
        ...loaded,
      ]);
      setItems(enrichItems(workflowMetricsFromMessages(refreshed), refreshed));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err?.message || 'Failed to load more messages',
      }));
    } finally {
      setLoadingMore(false);
    }
  }, [channel?.id, dispatch, enrichItems, hasMoreMessages, loadingMore]);

  useEffect(() => {
    const onChanged = (event) => {
      const messageId = event?.detail?.messageId;
      if (messageId && upsertMetricsItem(messageId)) return;
      fetchMetrics({ silent: true });
    };
    window.addEventListener('workloop:workflows-assigned-changed', onChanged);
    return () => window.removeEventListener('workloop:workflows-assigned-changed', onChanged);
  }, [fetchMetrics, upsertMetricsItem]);

  const raisedByOptions = useMemo(() => {
    const seen = new Map();
    items.forEach((item) => {
      const id = getItemSenderId(item);
      if (!seen.has(id)) {
        seen.set(id, { id, name: item.senderName || 'Unknown' });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const assignedToOptions = useMemo(() => {
    const seen = new Map();
    items.forEach((item) => {
      const id = item.workflowAssignedToUserId;
      if (!id) return;
      if (!seen.has(id)) {
        seen.set(id, { id, name: item.workflowAssignedToName || 'Unknown' });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const tagOptions = useMemo(() => {
    const seen = new Map();
    items.forEach((item) => {
      (item.customTags || []).forEach((customTag) => {
        const label = String(customTag.label || customTag.id || '').trim();
        const key = label.toLowerCase();
        if (!key || seen.has(key)) return;
        seen.set(key, { id: key, label });
      });
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  const hasActiveFilters = dateMode !== 'all' || Boolean(raisedBy) || Boolean(assignedTo) || Boolean(workflowType)
    || Boolean(workflowStatus) || Boolean(tag)
    || (dateMode === 'between' && (dateFrom || dateTo))
    || groupBy !== 'workflow' || sortDir !== 'desc'
    || !showWorkflowItems || !showPersonalItems;

  const filteredItems = useMemo(() => {
    const today = todayKey();
    const fromTime = dateMode === 'between' && dateFrom
      ? new Date(`${dateFrom}T00:00:00`).getTime()
      : null;
    const toTime = dateMode === 'between' && dateTo
      ? new Date(`${dateTo}T23:59:59.999`).getTime()
      : null;

    return items.filter((item) => {
      if (raisedBy && getItemSenderId(item) !== raisedBy) return false;
      if (workflowType && getItemTypeId(item) !== workflowType) return false;
      if (workflowStatus && getItemStatusId(item) !== workflowStatus) return false;
      if (tag) {
        const tags = item.customTags || [];
        if (tag === UNTAGGED_ID) {
          if (tags.length) return false;
        } else if (!itemHasTag(item, tag)) {
          return false;
        }
      }
      if (assignedTo) {
        if (getItemAssigneeId(item) !== assignedTo) return false;
      }
      const isTypedWorkflow = getItemTypeId(item) !== OTHER_SECTION_ID;
      if (isTypedWorkflow && !showWorkflowItems) return false;
      if (!isTypedWorkflow && !showPersonalItems) return false;

      const date = parseItemDate(item);
      if (dateMode === 'today') {
        if (!date || localDateKey(date) !== today) return false;
      } else if (dateMode === 'between') {
        const time = date ? date.getTime() : 0;
        if (fromTime != null && (!date || time < fromTime)) return false;
        if (toTime != null && (!date || time > toTime)) return false;
      }
      return true;
    });
  }, [items, dateMode, dateFrom, dateTo, raisedBy, assignedTo, workflowType, workflowStatus, tag, showWorkflowItems, showPersonalItems]);

  const openItems = useMemo(
    () => {
      const query = assignedToMeSearch.trim().toLowerCase();
      const statusFilterOn = statusOpen || statusClosed;
      return filteredItems.filter((item) => {
        const closed = isItemClosed(item);
        if (statusFilterOn) {
          const matchesOpen = statusOpen && !closed;
          const matchesClosed = statusClosed && closed;
          if (!matchesOpen && !matchesClosed) return false;
        }
        if (query && !getItemSearchText(item).includes(query)) return false;
        return true;
      });
    },
    [filteredItems, statusOpen, statusClosed, assignedToMeSearch],
  );

  useEffect(() => {
    ensureWorkTimers();
    return subscribeWorkTimers(() => setWorkTimerTick((value) => value + 1));
  }, []);

  const sortedOpenItems = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...openItems].sort((a, b) => {
      const urgentDiff = compareUrgentFirst(a, b, { isUrgent: isOpenUrgentWorkflow });
      if (urgentDiff !== 0) return urgentDiff;
      const timerDiff = compareWorkTimerPriority('WORKFLOW', a.messageId, 'WORKFLOW', b.messageId);
      if (timerDiff !== 0) return timerDiff;
      return (itemTimestamp(a) - itemTimestamp(b)) * dir;
    });
  }, [openItems, sortDir, workTimerTick]);

  const sections = useMemo(
    () => buildGroupedSections(sortedOpenItems, groupBy, sortDir),
    [sortedOpenItems, groupBy, sortDir],
  );
  const totalCount = filteredItems.length;
  const completedCount = useMemo(() => countClosed(filteredItems), [filteredItems]);
  const pendingCount = totalCount - completedCount;

  const magicPickItems = useMemo(() => (
    (openItems || [])
      .filter((item) => !isItemClosed(item))
      .map((item) => {
        const title = item.workflowTitle || getTicketText(item.content);
        const descRaw = [item.workflowDescription, item.content]
          .find((entry) => typeof entry === 'string' && entry.trim()) || '';
        let subtitle = descRaw;
        if (subtitle.trim().startsWith('<')) {
          try {
            const d = document.createElement('div');
            d.innerHTML = subtitle;
            subtitle = (d.textContent || d.innerText || '').trim();
          } catch {
            subtitle = getTicketText(subtitle, 280);
          }
        }
        subtitle = String(subtitle || '').trim().slice(0, 280);
        const typeId = item.workflowByCategory?.['work-item-type'];
        const typeLabel = typeId ? getReactionDisplay(typeId)?.label : null;
        return {
          id: `workflow:${item.messageId}`,
          kind: 'workflow',
          title,
          subtitle: subtitle && subtitle !== title ? subtitle : '',
          meta: typeLabel || item.workflowAssignedToName || undefined,
          raw: item,
        };
      })
  ), [openItems]);

  useEffect(() => {
    setCollapsedSections({});
  }, [groupBy]);

  useEffect(() => {
    setFiltersOpen(false);
  }, [channel?.id]);

  const toggleSection = (sectionId) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const toggleWorkflowExpansion = (messageId) => {
    setExpandedWorkflowIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  };

  const handleCompleteWorkflow = async (item) => {
    if (!item?.messageId || !item?.channelId || !currentUserId) return;
    let linkedTasks = [];
    try {
      const existing = await dispatch(fetchTasks({ messageId: item.messageId })).unwrap();
      linkedTasks = workflowLinkedTasksFrom(existing);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to load workflow tasks'),
      }));
      return;
    }
    const mappedByTaskId = await loadMappedByTaskId(
      linkedTasks,
      (taskId) => dispatch(fetchTasksByMapTaskId(taskId)).unwrap(),
    );
    const { minutes, error } = await resolveWorkflowCloseMinutes({
      linkedTasks,
      mappedByTaskId,
      workflowTitle: item?.workflowTitle,
    });
    if (error) {
      dispatch(addNotification({ type: 'error', message: error }));
      return;
    }
    if (minutes == null) return;
    let succeeded = false;
    try {
      setCompletingWorkflowId(item.messageId);
      await dispatch(addReaction({
        messageId: item.messageId,
        emoji: WORKFLOW_CLOSED_STATUS_ID,
        channelId: item.channelId,
        userId: currentUserId,
        ...(Number(minutes) > 0 ? { completionTimeInMinutes: minutes } : {}),
      })).unwrap();
      dispatch(addNotification({ type: 'success', message: 'Workflow marked complete' }));
      window.dispatchEvent(new CustomEvent('workloop:workflows-assigned-changed', {
        detail: { messageId: item.messageId, action: 'completed', completionTimeInMinutes: minutes },
      }));
      succeeded = true;
      await new Promise((resolve) => window.setTimeout(resolve, 1100));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to complete workflow'),
      }));
    } finally {
      setCompletingWorkflowId(null);
    }
    if (succeeded) {
      await new Promise((resolve) => window.setTimeout(resolve, 520));
      setViewingWorkflow((prev) => (prev?.messageId === item.messageId ? null : prev));
    }
  };

  const handleDownload = async (attachment) => {
    try {
      await downloadAttachment(attachment);
    } catch (error) {
      openAttachmentFallback(attachment);
    }
  };

  const clearFilters = () => {
    setDateMode('all');
    setDateFrom('');
    setDateTo('');
    setRaisedBy('');
    setAssignedTo('');
    setStatusOpen(false);
    setStatusClosed(false);
    setAssignedToMeSearch('');
    setWorkflowType('');
    setWorkflowStatus('');
    setTag('');
    setGroupBy('workflow');
    setSortDir('desc');
    setShowWorkflowItems(true);
    setShowPersonalItems(true);
  };

  const handleAssignedToChange = (value) => {
    setAssignedTo(value);
  };

  const toggleAssignedToMe = () => {
    if (!currentUserId) return;
    setAssignedTo((prev) => (prev === currentUserId ? '' : currentUserId));
  };

  const toggleStatusOpen = () => setStatusOpen((prev) => !prev);

  const toggleStatusClosed = () => setStatusClosed((prev) => !prev);

  const viewingItem = items.find((item) => item.messageId === viewingWorkflow?.messageId) || viewingWorkflow;

  return (
    <div className="flex flex-col h-full min-h-0 bg-white dark:bg-gray-800">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center space-x-2 min-w-0">
          <svg className="w-5 h-5 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Management Metrics</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {totalCount} total · {pendingCount} pending · {completedCount} completed
            </p>
          </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            type="button"
            onClick={() => setMagicPickOpen(true)}
            disabled={loading || magicPickItems.length === 0}
            className="p-1.5 rounded-lg text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Pick a task for me"
            aria-label="Pick a task for me"
          >
            <MagicWandIcon className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={handleRefreshMetrics}
            disabled={loading || refreshing || !channel?.id}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh metrics"
            aria-label="Refresh management metrics"
          >
            <RefreshIcon className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          {typeof onToggleExpand === 'function' && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={isExpanded ? 'Shrink metrics' : 'Enlarge metrics'}
              aria-label={isExpanded ? 'Shrink metrics' : 'Enlarge metrics'}
              aria-pressed={isExpanded}
            >
              {isExpanded ? <MinimizeIcon /> : <MaximizeIcon />}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Close panel"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {!loading && !error && items.length > 0 && (
        <MetricsFilters
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          dateMode={dateMode}
          onDateModeChange={setDateMode}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          raisedBy={raisedBy}
          onRaisedByChange={setRaisedBy}
          raisedByOptions={raisedByOptions}
          assignedTo={assignedTo}
          onAssignedToChange={handleAssignedToChange}
          assignedToOptions={assignedToOptions}
          currentUserId={currentUserId}
          assignedToMeSearch={assignedToMeSearch}
          onAssignedToMeSearchChange={setAssignedToMeSearch}
          statusOpen={statusOpen}
          statusClosed={statusClosed}
          onStatusOpenChange={toggleStatusOpen}
          onStatusClosedChange={toggleStatusClosed}
          onToggleAssignedToMe={toggleAssignedToMe}
          workflowType={workflowType}
          onWorkflowTypeChange={setWorkflowType}
          workflowStatus={workflowStatus}
          onWorkflowStatusChange={setWorkflowStatus}
          tag={tag}
          onTagChange={setTag}
          tagOptions={tagOptions}
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          sortDir={sortDir}
          onSortDirChange={setSortDir}
          showWorkflowItems={showWorkflowItems}
          onShowWorkflowItemsChange={setShowWorkflowItems}
          showPersonalItems={showPersonalItems}
          onShowPersonalItemsChange={setShowPersonalItems}
          hasActiveFilters={hasActiveFilters}
          onClear={clearFilters}
        />
      )}

      <div className="flex-1 overflow-y-auto min-h-0">
        {loading && (
          <div className="flex items-center justify-center py-12 text-sm text-gray-500 dark:text-gray-400">
            Loading metrics…
          </div>
        )}
        {!loading && error && (
          <div className="text-center py-8 px-4">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <button
              type="button"
              onClick={() => fetchMetrics({ force: true })}
              className="mt-3 text-xs text-primary-600 dark:text-primary-400 hover:underline"
            >
              Retry
            </button>
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <div className="text-center py-12 px-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">No workflow messages found.</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Add workflow reactions (Bug, Task, Epic, etc.) to messages in this chat.
            </p>
          </div>
        )}
        {!loading && !error && items.length > 0 && sections.length === 0 && (
          <div className="text-center py-12 px-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {statusOpen && !statusClosed
                ? 'No open workflows match these filters.'
                : statusClosed && !statusOpen
                  ? 'No closed workflows match these filters.'
                  : 'No workflows match these filters.'}
            </p>
            {!(openItems.length === 0 && filteredItems.length > 0) ? (
              <button
                type="button"
                onClick={clearFilters}
                className="mt-3 text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
              >
                Clear filters
              </button>
            ) : null}
          </div>
        )}
        {!loading && !error && sections.length > 0 && (
          <div className="space-y-3 p-3">
            <OverviewMetricsChart
              total={totalCount}
              pending={pendingCount}
              completed={completedCount}
              expanded={isExpanded}
            />
            {sections.map((section) => {
              const isCollapsed = collapsedSections[section.id];
              const isBugSection = section.id === BUG_TYPE_ID;
              const sectionCompleted = countClosed(section.items);
              const sectionPending = section.count - sectionCompleted;
              return (
                <section
                  key={section.id}
                  className={`rounded-2xl border overflow-hidden bg-white dark:bg-gray-800 shadow-sm ${
                    isBugSection
                      ? 'border-red-200 dark:border-red-900/50'
                      : 'border-gray-200/80 dark:border-gray-700/80'
                  }`}
                >
                  <div className={`flex items-center gap-2 px-3.5 py-2.5 border-b border-gray-100 dark:border-gray-700/80 ${
                    isBugSection ? 'bg-red-50/40 dark:bg-red-950/15' : 'bg-gray-50/70 dark:bg-gray-800/60'
                  }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSection(section.id)}
                      className="flex-1 min-w-0 flex items-center gap-2 text-left"
                    >
                      <span className="text-xl leading-none flex-shrink-0" aria-hidden="true">
                        {section.emoji}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h4 className={`text-[15px] font-bold tracking-tight truncate ${
                          isBugSection ? 'text-red-800 dark:text-red-200' : 'text-gray-900 dark:text-white'
                        }`}
                        >
                          {section.label}
                        </h4>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400">
                          {section.count} total · {sectionPending} pending · {sectionCompleted} completed
                        </p>
                      </div>
                      <ChevronDownIcon
                        className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                      />
                    </button>
                    <SectionStatusPie
                      items={section.items}
                      size={52}
                      innerRadius={14}
                      outerRadius={24}
                      showLegend={false}
                    />
                  </div>
                  {!isCollapsed && (
                    <div className="divide-y divide-gray-100/90 dark:divide-gray-700/60">
                      {section.items.map((item) => {
                        const isClosed = isItemClosed(item);
                        const isUrgent = isOpenUrgentWorkflow(item);
                        const isRowExpanded = expandedWorkflowIds.has(item.messageId);
                        const typeId = item.workflowByCategory?.['work-item-type'];
                        const statusId = item.workflowByCategory?.['workflow-status'];
                        const typeDisplay = typeId ? getReactionDisplay(typeId) : null;
                        const statusDisplay = statusId ? getReactionDisplay(statusId) : null;
                        const ticketText = item.workflowTitle || getTicketText(item.content);
                        const Row = isUrgent && !isClosed ? UrgentRowShell : 'div';
                        const rowProps = isUrgent && !isClosed
                          ? {
                              deadlineAt: item.urgentDeadlineAt,
                              className: `group ${isClosed ? 'opacity-75' : ''}`,
                            }
                          : {
                              className: `group w-full min-w-0 transition-colors bg-white dark:bg-gray-800 hover:bg-orange-50/50 dark:hover:bg-gray-700/30 ${
                                isClosed ? 'opacity-75' : ''
                              }`,
                            };
                        return (
                          <Row key={item.messageId} {...rowProps}>
                            <div className="flex items-start gap-2.5 px-3.5 py-3 min-w-0">
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={() => setViewingWorkflow(item)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    setViewingWorkflow(item);
                                  }
                                }}
                                className="min-w-0 flex-1 text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/25 rounded-md"
                              >
                                <div className="flex flex-wrap items-center gap-1 min-w-0">
                                  {typeDisplay ? (
                                    <span className={`${chipBase} ${chipTone(typeDisplay.tone)}`}>
                                      <span aria-hidden="true">{typeDisplay.emoji}</span>
                                      <span className="truncate">{typeDisplay.label}</span>
                                    </span>
                                  ) : (
                                    <span className={`${chipBase} ${chipTone('slate')}`}>Workflow</span>
                                  )}
                                  {statusDisplay && (
                                    <span className={`${chipBase} ${chipTone(statusDisplay.tone || 'teal')}`}>
                                      <span aria-hidden="true">{statusDisplay.emoji}</span>
                                      <span className="truncate">{statusDisplay.label}</span>
                                    </span>
                                  )}
                                  {isUrgent && !isClosed ? (
                                    <UrgentTag deadlineAt={item.urgentDeadlineAt} />
                                  ) : null}
                                  {isUrgent && !isClosed && item.urgentDeadlineAt ? (
                                    <UrgentCountdownBadge deadlineAt={item.urgentDeadlineAt} compact />
                                  ) : null}
                                  {item.workflowTrackId ? (
                                    <span className={`${chipBase} ${chipTone('indigo')}`}>
                                      {item.workflowTrackId}
                                    </span>
                                  ) : null}
                                  {isClosed ? (
                                    <span className="text-[10px] text-gray-400 italic flex-shrink-0">Done</span>
                                  ) : null}
                                </div>
                                <p className="mt-1.5 min-w-0 flex-1 text-[15px] font-bold tracking-tight text-gray-900 dark:text-white leading-snug truncate">
                                  {ticketText}
                                </p>
                                <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                  {item.senderName || 'Unknown'}
                                  {formatRaisedAt(item) ? ` · ${formatRaisedAt(item)}` : ''}
                                </p>
                              </div>
                              {!isClosed && (
                                <div className="flex-shrink-0 self-center" onClick={(event) => event.stopPropagation()}>
                                  <WorkItemTimer
                                    targetType="WORKFLOW"
                                    targetId={item.messageId}
                                    noun="workflow"
                                    compact
                                  />
                                </div>
                              )}
                              {!isClosed && (
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleCompleteWorkflow(item);
                                  }}
                                  disabled={completingWorkflowId === item.messageId}
                                  className="mt-0.5 p-1.5 rounded-lg text-gray-400 hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-900/30 dark:hover:text-emerald-300 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto disabled:opacity-50"
                                  title="Mark workflow as complete"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleWorkflowExpansion(item.messageId);
                                }}
                                className="mt-0.5 p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors flex-shrink-0"
                                title={isRowExpanded ? 'Hide description' : 'Show description'}
                                aria-expanded={isRowExpanded}
                              >
                                <ChevronDownIcon
                                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                                    isRowExpanded ? '' : '-rotate-90'
                                  }`}
                                />
                              </button>
                            </div>
                            {isRowExpanded && (
                              <div
                                className="border-t border-gray-100 dark:border-gray-700/80 px-3.5 py-2.5 min-w-0"
                                onClick={(event) => event.stopPropagation()}
                              >
                                {renderDescription(item)}
                              </div>
                            )}
                          </Row>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>

      {!loading && !error && (
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 px-4 py-2.5">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={!hasMoreMessages || loadingMore}
            className="w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-[13px] font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/80 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-800 transition-colors"
          >
            {loadingMore
              ? 'Loading next 500 messages…'
              : hasMoreMessages
                ? 'Load more'
                : 'No more workflows'}
          </button>
        </div>
      )}

      <MagicPickModal
        isOpen={magicPickOpen}
        onClose={() => setMagicPickOpen(false)}
        items={magicPickItems}
        onOpenItem={(picked) => {
          if (picked?.raw) setViewingWorkflow(picked.raw);
        }}
      />

      {viewingItem && (
        <WorkflowViewDialog
          item={viewingItem}
          onClose={() => setViewingWorkflow(null)}
          onComplete={
            Object.values(viewingItem.workflowByCategory || {}).includes(WORKFLOW_CLOSED_STATUS_ID)
              ? undefined
              : () => handleCompleteWorkflow(viewingItem)
          }
          completing={completingWorkflowId === viewingItem.messageId}
          onNavigateToChat={() => {
            setViewingWorkflow(null);
            onMessageClick?.(viewingItem);
          }}
          onViewImage={(images, index) => setGallery({ images, index })}
          onDownload={handleDownload}
        />
      )}
      {gallery && (
        <ImageViewerModal
          images={gallery.images}
          initialIndex={gallery.index}
          onClose={() => setGallery(null)}
          onDownload={handleDownload}
        />
      )}
      {completionTimeDialog}
    </div>
  );
};

export default ManagementMetricsPanel;
